import { useState } from 'react';
import { useLocation, Link } from 'wouter';
import { ChevronDown, ArrowRight, Mail } from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { usePageSEO } from '../utils/seo';

export interface FaqItem {
    question: string;
    answer: string;
    category: 'Operations & Setup' | 'Carriers & Booking' | 'Trial & Pricing';
}

export const BAYWHIZ_FAQ_ITEMS: FaqItem[] = [
    {
        category: 'Operations & Setup',
        question: 'Do we need to replace our WMS or complete an integration to use BayWhiz?',
        answer:
            'No. BayWhiz works alongside your existing warehouse management system or ERP. You can configure your facility, operating hours, and unlimited dock doors directly in BayWhiz and create or import Purchase Orders via CSV (or Excel .xlsx on the 30-day trial and Operations plan) without launching an IT integration project.',
    },
    {
        category: 'Carriers & Booking',
        question: 'How do carriers request dock appointments?',
        answer:
            'Each warehouse facility receives a unique public booking link. Carriers enter a valid PO number (or Load number on Operations), select from your available operating time slots, and submit their carrier, driver, and equipment details. Requests enter your warehouse approval queue for manual review—or, on the 30-day trial and Operations plan, can be evaluated by your configurable rule-based automatic approval criteria.',
    },
    {
        category: 'Carriers & Booking',
        question: 'Do freight carriers have to pay or create an account to book?',
        answer:
            'No. Carriers never pay to submit appointment requests or use the Carrier Portal. Carriers can submit a request as a guest using your public booking link and optionally create a free Carrier Portal account or claim their booking with a secure signed link to view status updates or cancel.',
    },
    {
        category: 'Operations & Setup',
        question: 'What happens when a carrier cancels an approved appointment?',
        answer:
            'When a carrier cancels an approved appointment, BayWhiz immediately releases the assigned dock door so the slot becomes available again, while preserving the historical record of who approved the appointment, which dock doors were assigned, and when the carrier cancelled.',
    },
    {
        category: 'Operations & Setup',
        question: 'How does BayWhiz track check-in, checkout, and dwell time?',
        answer:
            'Warehouse staff record when a truck arrives at the yard (Check-In), when it is dispatched to a dock door, receiving pallet counts and discrepancies for linked POs, and when unloading finishes (Complete / Check-Out). Both Starter and Operations record complete appointment timestamps and schedule CSV exports, while the 30-day free trial and Operations plan ($199 USD/month) also include advanced dwell and detention analytics and carrier reliability scorecards.',
    },
    {
        category: 'Trial & Pricing',
        question: 'Is a credit card required to start the 30-day free trial?',
        answer:
            'No credit card is required to register or use your 30-day trial. Every new warehouse receives full Operations feature access—including unlimited dock doors and warehouse users—to configure the facility and manage real appointments for 30 calendar days with zero automatic billing.',
    },
    {
        category: 'Trial & Pricing',
        question: 'What happens when our 30-day trial expires?',
        answer:
            'Because no credit card is required at signup, there is no automatic charge at trial expiration. If no paid subscription is activated by the end of day 30, operational access pauses and warehouse managers can contact support@baywhiz.com to activate Starter or Operations. Your docks, POs, and appointment history remain saved.',
    },
    {
        category: 'Trial & Pricing',
        question: 'How is BayWhiz priced after the free trial?',
        answer:
            `Subscriptions are billed monthly in United States Dollars (USD) per warehouse facility: Starter is $${PRICING_CONFIG.plans.starter.priceMonthlyUsd} USD/month per facility and Operations is $${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/month per facility after your 30-day full-access trial. Both plans include unlimited dock doors and warehouse users, and there is no automatic charge at expiration—contact ${PRICING_CONFIG.supportEmail} to activate a paid plan.`,
    },
    {
        category: 'Trial & Pricing',
        question: 'What is the difference between the Starter ($99/mo) and Operations ($199/mo) plans?',
        answer:
            'Both plans include unlimited dock doors and warehouse users. Starter ($99 USD/month per facility) includes the live warehouse schedule and dock board, carrier booking requests and PO validation, manual appointment approval and management, dock assignment/reassignment, check-in and checkout, Purchase Order management, CSV PO import, basic receiving and discrepancy recording, appointment history with schedule CSV export, and the carrier contact directory. Operations ($199 USD/month per facility) includes everything in Starter plus rule-based automatic appointment approval, Consolidated Loads and linked outbound Sales Orders, Excel (.xlsx) PO import, advanced dwell and detention analytics, carrier reliability scorecards, advanced receiving exception analysis and shortage follow-up POs, historical delay-risk analysis and rule-based operational assistance, and the deterministic Copilot for operational queries.',
    },
];

const FaqPage = () => {
    const [, setLocation] = useLocation();
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    usePageSEO({
        title: 'Frequently Asked Questions | BayWhiz Dock Scheduling',
        description:
            'Answers to common questions about BayWhiz warehouse dock scheduling, carrier self-service booking, PO validation, dwell tracking, and the 30-day free trial.',
        canonicalPath: '/faq',
    });

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
            <MarketingNav variant="light" />

            <main className="flex-1">
                {/* Header */}
                <section className="pt-16 pb-12 px-6 bg-white border-b border-slate-200">
                    <div className="max-w-3xl mx-auto text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold uppercase tracking-wider mb-4">
                            Frequently Asked Questions
                        </span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
                            Clear answers about how BayWhiz works.
                        </h1>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Everything warehouse managers ask about dock setup, carrier appointment requests, dwell tracking, and our 30-day free trial.
                        </p>
                    </div>
                </section>

                {/* Accordion List */}
                <section className="py-16 px-6">
                    <div className="max-w-3xl mx-auto space-y-4">
                        {BAYWHIZ_FAQ_ITEMS.map((item, idx) => {
                            const isOpen = openIndex === idx;
                            const panelId = `faq-panel-${idx}`;
                            const buttonId = `faq-button-${idx}`;
                            return (
                                <div
                                    key={item.question}
                                    className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                                >
                                    <button
                                        id={buttonId}
                                        type="button"
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => setOpenIndex(isOpen ? null : idx)}
                                        className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-500"
                                    >
                                        <div>
                                            <span className="block text-[11px] font-bold uppercase tracking-wider text-teal-700 mb-1">
                                                {item.category}
                                            </span>
                                            <span className="text-base sm:text-lg font-bold text-slate-900">
                                                {item.question}
                                            </span>
                                        </div>
                                        <ChevronDown
                                            size={20}
                                            className={`text-slate-400 shrink-0 mt-1 transition-transform duration-200 ${
                                                isOpen ? 'rotate-180 text-teal-600' : ''
                                            }`}
                                        />
                                    </button>
                                    {isOpen && (
                                        <div
                                            id={panelId}
                                            role="region"
                                            aria-labelledby={buttonId}
                                            className="px-6 pb-5 pt-1 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100"
                                        >
                                            {item.answer}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Still have questions + Trial CTA */}
                <section className="py-16 px-6 bg-white border-t border-slate-200">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                            Have a question about your warehouse workflow?
                        </h2>
                        <p className="text-slate-600 mb-8">
                            Reach our team directly or start a 30-day free trial to test BayWhiz with your own dock doors and appointment schedule.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                type="button"
                                onClick={() => setLocation('/request-trial')}
                                className="w-full sm:w-auto h-12 px-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-sm flex items-center justify-center gap-2"
                            >
                                Request Free Trial
                                <ArrowRight size={16} />
                            </button>
                            <Link
                                href="/contact"
                                className="w-full sm:w-auto h-12 px-6 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-base flex items-center justify-center gap-2"
                            >
                                <Mail size={16} />
                                Contact Support
                            </Link>
                        </div>
                        <p className="text-xs text-slate-500 mt-4">
                            {PRICING_CONFIG.trialSummary.headline}
                        </p>
                    </div>
                </section>
            </main>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default FaqPage;
